import type { NotificationMessageContext } from 'src/api/generated';

export const NOTIFICATION_PLATFORM = {
  IOS: 'IOS',
  ANDROID: 'ANDROID',
  WEB_PUSH: 'WEB_PUSH',
  IOS_VOIP: 'IOS_VOIP',
  ANDROID_VOIP: 'ANDROID_VOIP',
} as const;

export type NotificationPlatform =
  (typeof NOTIFICATION_PLATFORM)[keyof typeof NOTIFICATION_PLATFORM];

export interface NotificationWebPushKeys {
  p256dh: string;
  auth: string;
}

export interface NotificationWebPushSubscription {
  endpoint: string;
  keys: NotificationWebPushKeys;
  vapidKeyId?: string;
}

export interface NotificationEndpointCreateBodyParams {
  platform: NotificationPlatform;
  deviceId: string;
  deviceName?: string;
  locale?: string;
  webPush: NotificationWebPushSubscription;
}

export type NotificationPlatformEnvironment = 'sandbox' | 'production';

export interface NotificationEndpointCapabilities {
  messaging: boolean;
  marketing: boolean;
  calls: boolean;
}

export interface NotificationEndpoint {
  id: string;
  tenantId: string;
  userId: string;
  platform: NotificationPlatform;
  deviceId: string;
  deviceName?: string;
  appVersion?: string;
  osVersion?: string;
  locale?: string;
  token?: string;
  webPush?: NotificationWebPushSubscription;
  bundleId?: string;
  packageName?: string;
  environment?: NotificationPlatformEnvironment;
  capabilities?: NotificationEndpointCapabilities;
  isPrimary?: boolean;
  isEnabled: boolean;
  lastActiveAt?: string;
  createdAt: string;
  updatedAt: string;
}

export type NotificationsEndpointsListResponse = NotificationEndpoint[];

export const NOTIFICATION_ROUTING_MODE = {
  RING_ALL: 'RING_ALL',
  PRIMARY_ONLY: 'PRIMARY_ONLY',
  LAST_ACTIVE: 'LAST_ACTIVE',
} as const;

export type NotificationRoutingMode =
  (typeof NOTIFICATION_ROUTING_MODE)[keyof typeof NOTIFICATION_ROUTING_MODE];

export interface NotificationChannelPreferences {
  messaging: boolean;
  marketing: boolean;
  calls: boolean;
}

export interface NotificationRoutingPreferences {
  mode: NotificationRoutingMode;
  primaryEndpointId?: string;
}

export const NOTIFICATION_DND_BEHAVIOR = {
  SUPPRESS_PUSH: 'SUPPRESS_PUSH',
  SUPPRESS_ALL: 'SUPPRESS_ALL',
  ALLOW_BADGE_ONLY: 'ALLOW_BADGE_ONLY',
  ALLOW_CALL_RING: 'ALLOW_CALL_RING',
  ALLOW_ALL: 'ALLOW_ALL',
} as const;

export type NotificationDndBehavior =
  (typeof NOTIFICATION_DND_BEHAVIOR)[keyof typeof NOTIFICATION_DND_BEHAVIOR];

export const NOTIFICATION_DND_CHANNEL = {
  MESSAGING: 'MESSAGING',
  MARKETING: 'MARKETING',
  CALLS: 'CALLS',
} as const;

export type NotificationDndChannel =
  (typeof NOTIFICATION_DND_CHANNEL)[keyof typeof NOTIFICATION_DND_CHANNEL];

export const NOTIFICATION_DND_DAY = {
  MON: 'MON',
  TUE: 'TUE',
  WED: 'WED',
  THU: 'THU',
  FRI: 'FRI',
  SAT: 'SAT',
  SUN: 'SUN',
} as const;

export type NotificationDndDay =
  (typeof NOTIFICATION_DND_DAY)[keyof typeof NOTIFICATION_DND_DAY];

export interface NotificationDndBehaviorConfig {
  default?: NotificationDndBehavior;
  perChannel?: Partial<Record<NotificationDndChannel, NotificationDndBehavior>>;
  createInboxItem?: boolean;
}

export interface NotificationDndSchedule {
  id: string;
  name?: string;
  enabled?: boolean;
  daysOfWeek: NotificationDndDay[];
  start: string;
  end: string;
  startDate?: string;
  endDate?: string;
  channels?: NotificationDndChannel[];
  behavior?: NotificationDndBehavior;
  priority?: number;
}

export interface NotificationDndDeviceOverride {
  endpointId: string;
  behavior?: NotificationDndBehavior;
  channels?: NotificationDndChannel[];
}

export interface NotificationDnd {
  enabled?: boolean;
  timezone?: string;
  snoozeUntil?: string;
  behavior?: NotificationDndBehaviorConfig;
  schedules?: NotificationDndSchedule[];
  deviceOverrides?: NotificationDndDeviceOverride[];
}

export interface NotificationInAppPreferences {
  showAlert: boolean;
  playSound: boolean;
}

export interface NotificationPreferences {
  tenantId: string;
  userId: string;
  channels: NotificationChannelPreferences;
  routing: NotificationRoutingPreferences;
  inApp: NotificationInAppPreferences;
  eventRules?: NotificationEventRule[];
  dnd?: NotificationDnd;
  updatedAt: string;
}

export interface NotificationPreferencesUpdateBodyParams {
  channels?: NotificationChannelPreferences;
  routing?: NotificationRoutingPreferences;
  inApp?: Partial<NotificationInAppPreferences>;
  dnd?: NotificationDnd;
  eventRules?: NotificationEventRule[];
}

export const NOTIFICATION_CHANNEL = {
  MESSAGING: 'MESSAGING',
  MARKETING: 'MARKETING',
  CALLS: 'CALLS',
  SYSTEM: 'SYSTEM',
} as const;

export type NotificationChannel =
  (typeof NOTIFICATION_CHANNEL)[keyof typeof NOTIFICATION_CHANNEL];

export const NOTIFICATION_DELIVERY_CHANNEL = {
  IN_APP: 'IN_APP',
  PUSH: 'PUSH',
  EMAIL: 'EMAIL',
  SMS: 'SMS',
  WHATSAPP: 'WHATSAPP',
} as const;

export type NotificationDeliveryChannel =
  (typeof NOTIFICATION_DELIVERY_CHANNEL)[keyof typeof NOTIFICATION_DELIVERY_CHANNEL];

export const NOTIFICATION_TYPE = {
  MESSAGE: 'MESSAGE',
  ASSIGNMENT: 'ASSIGNMENT',
  CAMPAIGN: 'CAMPAIGN',
  SYSTEM: 'SYSTEM',
} as const;

export type NotificationType =
  (typeof NOTIFICATION_TYPE)[keyof typeof NOTIFICATION_TYPE];

export const NOTIFICATION_EVENT_CATEGORY = {
  APP: 'APP',
  MARKETING: 'MARKETING',
} as const;

export type NotificationEventCategory =
  (typeof NOTIFICATION_EVENT_CATEGORY)[keyof typeof NOTIFICATION_EVENT_CATEGORY];

export const NOTIFICATION_EVENT_KEY = {
  MESSAGING_MESSAGE_RECEIVED: 'messaging.message.received',
  CONVERSATION_ESCALATED: 'conversation.escalated',
  CONVERSATION_MARKED_READY_TO_VISIT: 'conversation.marked-ready-to-visit',
  CONVERSATION_TABLE_EXPORT_READY: 'conversation.table-export.ready',
  MARKETING_CAMPAIGN_DISPATCH: 'marketing.campaign.dispatch',
} as const;

export type NotificationEventKey =
  (typeof NOTIFICATION_EVENT_KEY)[keyof typeof NOTIFICATION_EVENT_KEY];

export interface NotificationEventRule {
  eventKey: NotificationEventKey;
  enabled?: boolean;
  inApp?: Partial<NotificationInAppPreferences>;
  routing?: NotificationRoutingPreferences;
  deliveryChannels?: NotificationDeliveryChannel[];
}

export interface NotificationEventDefinition {
  eventKey: NotificationEventKey;
  category: NotificationEventCategory;
  channel: NotificationChannel;
  type: NotificationType;
  label: string;
  description?: string;
  labelKey?: string;
  descriptionKey?: string;
  defaultEnabled?: boolean;
  availableDeliveryChannels?: NotificationDeliveryChannel[];
  defaultDeliveryChannels?: NotificationDeliveryChannel[];
}

export interface NotificationsEventsListResponse {
  items: NotificationEventDefinition[];
}

export interface NotificationInboxMetadata {
  /** Canonical message context supplied by the notification producer. */
  message?: NotificationMessageContext;
  funnelConversationId?: number;
  messagingConversationId?: string;
  deepLink?: string;
  links?: {
    label: string;
    url: string;
  }[];
}

export interface NotificationInboxItem {
  id: string;
  tenantId: string;
  userId: string;
  title: string;
  body: string;
  channel: NotificationChannel;
  type: NotificationType;
  createdAt: string;
  readAt?: string;
  metadata?: NotificationInboxMetadata;
}

export interface NotificationsInboxListQueryParams {
  read?: boolean;
  channel?: NotificationChannel;
  type?: NotificationType;
  limit?: number;
  pageToken?: string;
  includeUnreadCount?: boolean;
}

export interface NotificationsInboxListResponse {
  items: NotificationInboxItem[];
  nextPageToken?: string;
  totalCount?: number;
  unreadCount?: number;
}

export interface NotificationsInboxMarkReadBodyParams {
  inboxIds?: string[];
  readAll?: boolean;
  readBefore?: string;
}

export interface NotificationsInboxMarkReadResponse {
  updatedCount: number;
}

export interface NotificationsInboxUnreadCountQueryParams {
  channel?: NotificationChannel;
  type?: NotificationType;
}

export interface NotificationsInboxUnreadCountResponse {
  unreadCount: number;
}
